import { inject } from '@angular/core';
import { GROUNDWORKS_AUTH_CONFIG, MsalService } from '@groundworks/ngx-groundworks-auth';
import { HttpTransportType, HubConnection, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ConfigService } from '../config.service';
import { HUB_CONNECTION_FACTORY } from './hub-connection.factory';

export abstract class BaseSignalRService {
  protected readonly configService = inject(ConfigService);
  protected readonly msalService = inject(MsalService);
  private readonly authConfig = inject(GROUNDWORKS_AUTH_CONFIG);
  /** The API root the SDK and the interceptor use: trimmed, with no trailing slash. */
  protected readonly apiUrl = this.authConfig.apiUrl;
  private readonly hubConnectionFactory = inject(HUB_CONNECTION_FACTORY);
  protected hubConnection: HubConnection | null = null;
  protected readonly connectionStateSubject = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);

  public connectionState$ = this.connectionStateSubject.asObservable();

  constructor() {
    // Building the hub needs no account or token, so it happens synchronously here.
    this.initializeConnection();
  }

  /**
   * Called from the base constructor, before the subclass's own fields are initialized: build the
   * URL from base members only.
   */
  protected abstract getHubUrl(): string;

  /**
   * Called from the base constructor, before the subclass's own fields are initialized: reference
   * them only inside the handler callbacks, which run later.
   */
  protected abstract setupEventHandlers(): void;

  protected initializeConnection(): void {
    const hubUrl = this.getHubUrl();

    this.hubConnection = this.hubConnectionFactory(hubUrl, {
      // Called on every connect and reconnect: MSAL serves the cached token or renews it.
      // acquireAccessToken() also checks for an active account, so a missing one fails the start
      // (or reconnect) rather than the build.
      accessTokenFactory: () => this.acquireAccessToken(),
      transport: HttpTransportType.WebSockets |
        HttpTransportType.ServerSentEvents |
        HttpTransportType.LongPolling
    });

    this.setupEventHandlers();
  }

  protected setupConnectionStateHandlers(): void {
    if (!this.hubConnection) return;

    this.hubConnection.onreconnecting((error) => {
      console.warn('SignalR connection reconnecting:', error);
      this.connectionStateSubject.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected(() => {
      this.connectionStateSubject.next(HubConnectionState.Connected);
    });

    this.hubConnection.onclose((error) => {
      if (error) {
        console.error('SignalR connection closed with error:', error);
      }
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
    });
  }

  async startConnection(): Promise<void> {
    if (!this.hubConnection) {
      this.initializeConnection();
    }

    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Disconnected) {
      try {
        await this.hubConnection.start();
        this.connectionStateSubject.next(HubConnectionState.Connected);
      } catch (error) {
        // Automatic reconnect covers only a connection that has started, so the hub stays down until
        // startConnection() runs again. A token failure was logged by acquireAccessToken().
        console.error('Failed to start the SignalR connection:', error);
        this.connectionStateSubject.next(HubConnectionState.Disconnected);
      }
    }
  }

  private async acquireAccessToken(): Promise<string> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        throw new Error('No active account found');
      }

      const tokenResponse = await firstValueFrom(this.msalService.acquireTokenSilent({
        scopes: [this.authConfig.apiScope],
        account: account
      }));
      return tokenResponse.accessToken;
    } catch (error) {
      console.error('Failed to acquire a token for the SignalR connection:', error);
      throw error;
    }
  }

  async stopConnection(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      try {
        await this.hubConnection.stop();
        this.connectionStateSubject.next(HubConnectionState.Disconnected);
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
        this.connectionStateSubject.next(HubConnectionState.Disconnected);
      }
    }
  }

  getConnectionState(): HubConnectionState {
    return this.hubConnection?.state ?? HubConnectionState.Disconnected;
  }

  isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }
}
